import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { FunnelStatusEditCard } from '../../molecules/funnels/statuses/status-edit-card';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { FunnelStatusPermission } from 'src/modules/shared/types/permissions.type';
import { FunnelStatusForm } from 'src/modules/settings/components/molecules/funnels/statuses/status-form';
import type { FunnelStatusFormValues } from 'src/modules/settings/components/molecules/funnels/statuses/status-form';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useEffect, useMemo, useState } from 'react';
import { PiPlus } from 'react-icons/pi';
import {
  useCreateFunnelStatus,
  useBulkUpdateFunnelStatuses,
} from 'src/modules/shared/hooks/api/crm/funnels/funnel-status.mutation';
import { DraggableColumnList } from 'src/modules/shared/components/molecules/draggable-column-list';
import { useColumnDnd } from 'src/modules/shared/hooks/board/column-dnd';
import { FunnelStatusCard } from 'src/modules/settings/components/atoms/funnels/statuses/status-card';
import {
  FunnelStatusTerminalOutcome,
  type FunnelStatus,
} from 'src/api/generated';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useDirtyMap } from 'src/modules/shared/hooks/dirty-state';
import { type StatusReference } from 'src/modules/settings/types/funnels/statuses/status-description.types';
import { useAiWorkspaceStatusIdsByStatus } from 'src/modules/shared/hooks/api/automations/employees/workspaces-ai';
import { buildFunnelStatusReorderUpdates } from '../../../utils/funnels/statuses/status-reorder.util';

const DIRTY_KEY_NEW_STATUS = '__new__' as const;
const DIRTY_KEY_STATUS = (id: number | string) => String(id);

interface FunnelStatusManagementPanelProps {
  funnelId: number;
  isEditing: boolean;
  onHasUnsavedChanges?: (dirty: boolean) => void;
}

export const FunnelStatusManagementPanel = ({
  funnelId,
  isEditing = false,
  onHasUnsavedChanges,
}: FunnelStatusManagementPanelProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const { setDirty, resetKey, resetAll, isAnyDirty } = useDirtyMap();

  const statusQuery = createQuery()
    .equals('funnelId', funnelId)
    .orderByAsc('position')
    .build();

  const {
    statuses,
    loading: isLoading,
    error,
    refetch,
  } = useFunnelStatuses(statusQuery);
  const statusIds = useMemo(
    () => statuses.map((status) => status.id),
    [statuses]
  );
  const { aiStatusIds } = useAiWorkspaceStatusIdsByStatus(
    statusIds,
    statusIds.length > 0
  );

  const { hasPermission: canCreate } = useHasPermissions(
    FunnelStatusPermission.Create
  );

  const { hasPermission: canUpdate } = useHasPermissions(
    FunnelStatusPermission.Update
  );
  const { hasPermission: canManageSystemStatuses } = useHasPermissions(
    FunnelStatusPermission.ManageSystem
  );

  const { createStatus, loading: isCreatingStatus } = useCreateFunnelStatus();
  const { bulkUpdateStatuses } = useBulkUpdateFunnelStatuses();

  const handleStatusesReorder = async (reorderedStatuses: FunnelStatus[]) => {
    const updates = buildFunnelStatusReorderUpdates(
      statuses,
      reorderedStatuses,
      canManageSystemStatuses
    );
    if (updates.length > 0) await bulkUpdateStatuses({ updates });
  };

  const {
    sensors,
    activeItem: activeStatus,
    handleDragStart,
    handleDragEnd,
    shouldAnimate,
  } = useColumnDnd<FunnelStatus>({
    items: statuses,
    getId: (status) => status.id,
    onItemsReordered: handleStatusesReorder,
  });

  const handleStartCreating = () => {
    setIsCreating(true);
  };

  const handleStopCreating = () => {
    setIsCreating(false);
    resetKey(DIRTY_KEY_NEW_STATUS);
  };

  const handleStatusCreate = async (data: FunnelStatusFormValues) => {
    const existingPositions = statuses.map((status) => status.position);
    const nextPosition = Math.max(0, ...existingPositions) + 1;

    await createStatus({
      ...data,
      terminalOutcome: data.terminalOutcome
        ? data.terminalOutcome === 'positive'
          ? FunnelStatusTerminalOutcome.POSITIVE
          : FunnelStatusTerminalOutcome.NEGATIVE
        : null,
      funnelId,
      position: nextPosition,
    });

    setIsCreating(false);
    resetKey(DIRTY_KEY_NEW_STATUS);
    resetAll();
  };

  useEffect(() => {
    if (isEditing) return;

    setIsCreating(false);
    resetAll();
  }, [isEditing, resetAll]);

  useEffect(() => {
    onHasUnsavedChanges?.(isAnyDirty);
  }, [isAnyDirty, onHasUnsavedChanges]);

  const statusReferences: StatusReference[] = useMemo(
    () =>
      statuses
        .filter((status) => !!status.name)
        .map((status) => ({
          id: status.id,
          name: status.name,
        })),
    [statuses]
  );
  const orderedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.position - b.position),
    [statuses]
  );

  const columnWidthClass = 'w-[540px]';

  if (isLoading) {
    return (
      <div className="flex gap-4">
        <CardSkeleton content={{ lines: 2 }} />
        <CardSkeleton content={{ lines: 2 }} />
        <CardSkeleton content={{ lines: 2 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <ErrorState
          // TODO: check when implementing i18n
          title="Ocurrió un error al cargar los estados"
          description="Por favor, inténtalo de nuevo"
          onAction={refetch}
          actionContent="Reintentar"
        />
      </div>
    );
  }

  if (!statuses.length && !isCreating) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <EmptyState
          // TODO: check when implementing i18n
          title="No hay estados"
          description="Agrega estados a este embudo para comenzar"
          size="sm"
          actionContent={
            <>
              <PiPlus className="w-4 h-4" />
              {/* TODO: check when implementing i18n */}
              <Text size="small">Nuevo estado</Text>
            </>
          }
          onAction={isEditing ? handleStartCreating : undefined}
        />
      </div>
    );
  }

  const canReorder = canUpdate && isEditing;

  return (
    <div className="flex gap-4">
      {statuses.length > 0 && (
        <DraggableColumnList
          items={orderedStatuses}
          columnClassName={columnWidthClass}
          renderItem={(status, { isDragging, DragHandle }) => (
            <FunnelStatusEditCard
              key={`${status.id}-${isEditing ? 'edit' : 'view'}`}
              status={status}
              isEditing={isEditing}
              isDragging={isDragging}
              DragHandle={DragHandle}
              className={isDragging ? 'opacity-50' : ''}
              referenceStatuses={statusReferences}
              isAiLinked={aiStatusIds.includes(status.id)}
              hasUnsavedChanges={isAnyDirty}
              onDirtyChange={(dirty) =>
                setDirty(DIRTY_KEY_STATUS(status.id), dirty)
              }
            />
          )}
          getItemId={(status) => status.id}
          canReorder={canReorder}
          isItemReorderable={(status) =>
            canManageSystemStatuses || !status.systemKey
          }
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          activeItem={activeStatus}
          shouldAnimate={shouldAnimate}
          dragOverlay={(draggingStatus) => (
            <FunnelStatusEditCard
              key={`${draggingStatus.id}-${isEditing ? 'edit' : 'view'}`}
              status={draggingStatus}
              isEditing={isEditing}
              isDragging={true}
              isAiLinked={aiStatusIds.includes(draggingStatus.id)}
              DragHandle={({ children }) => <div>{children}</div>}
            />
          )}
        />
      )}

      {isCreating ? (
        <div className={`${columnWidthClass} shrink-0 pb-2`}>
          <FunnelStatusCard isAiLinked={false}>
            <FunnelStatusForm
              key={`new-status-form-${statuses.length}`}
              onSubmit={handleStatusCreate}
              isLoading={isCreatingStatus}
              showDelete={false}
              onCancel={handleStopCreating}
              hideActions
              isAiLinked={false}
              onDirtyChange={(dirty) => setDirty(DIRTY_KEY_NEW_STATUS, dirty)}
            />
          </FunnelStatusCard>
        </div>
      ) : (
        canCreate &&
        isEditing && (
          <div className="flex items-start justify-start">
            {/* TODO: check when implementing i18n */}
            <Tooltip content="Agrega un nuevo estado">
              <Button
                variant="secondary"
                appearance="outline"
                size="sm"
                isIconOnly
                onClick={handleStartCreating}
              >
                <PiPlus className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
};
